import { computed, ref } from 'vue'

const TOKEN_KEY = 'budget.auth.token'
const EXPIRES_KEY = 'budget.auth.expiresAt'
const ACCESS_KEY = 'budget.auth.access'

// authVersion is bumped whenever the stored session changes so that the
// reactive accessors returned by useAuth() re-evaluate. localStorage is not
// reactive on its own, so login/logout notify subscribers through this ref.
const authVersion = ref(0)

function notifyAuthChange() {
  authVersion.value++
}

export function storeSession(token, expiresAt, access) {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(EXPIRES_KEY, expiresAt)
  localStorage.setItem(ACCESS_KEY, access || 'readonly')
  notifyAuthChange()
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(EXPIRES_KEY)
  localStorage.removeItem(ACCESS_KEY)
  notifyAuthChange()
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY)
}

export function getAccess() {
  const raw = localStorage.getItem(ACCESS_KEY)
  if (raw === 'admin') {
    return 'admin'
  }

  return 'readonly'
}

export function isAdmin() {
  return getAccess() === 'admin'
}

export function canMutate() {
  return isAdmin()
}

export function isAuthenticated() {
  const token = getToken()
  if (!token) {
    return false
  }

  const expiresAt = localStorage.getItem(EXPIRES_KEY)
  if (!expiresAt) {
    return false
  }

  const expiresAtMs = Date.parse(expiresAt)
  if (Number.isNaN(expiresAtMs) || expiresAtMs <= Date.now()) {
    clearSession()
    return false
  }

  return true
}

// refreshSession exchanges a still-valid stored token for a fresh one, so the
// session expires only after JWT_SESSION_TTL without a visit. It returns
// 'unauthorized' when the server rejects the token (session cleared); network
// and other errors keep the current session untouched.
let pendingRefresh = null

export function refreshSession() {
  pendingRefresh = doRefreshSession().finally(() => {
    pendingRefresh = null
  })

  return pendingRefresh
}

// settleRefresh resolves once any in-flight refreshSession() has finished, so
// a request rejected with the pre-refresh token can wait for its replacement.
export function settleRefresh() {
  return pendingRefresh ?? Promise.resolve()
}

async function doRefreshSession() {
  if (!isAuthenticated()) {
    return undefined
  }

  const token = getToken()

  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })

    // A logout or re-login (here or in another tab) during the request owns
    // the session now; neither a success nor a 401 for the old token may touch it.
    if (getToken() !== token) {
      return undefined
    }

    if (res.status === 401) {
      clearSession()
      return 'unauthorized'
    }

    if (!res.ok) {
      return undefined
    }

    const body = await res.json()
    if (getToken() !== token) {
      return undefined
    }

    storeSession(body.token, body.expiresAt, body.access)
    return 'refreshed'
  } catch {
    return undefined
  }
}

// useAuth exposes the session state as reactive computeds. Reading any of
// them in a template re-evaluates only when the session changes (via
// storeSession/clearSession), instead of on every render like a bare
// isAdmin() call.
export function useAuth() {
  return {
    isAdmin: computed(() => (authVersion.value, isAdmin())),
    access: computed(() => (authVersion.value, getAccess())),
    token: computed(() => (authVersion.value, getToken())),
    isAuthenticated: computed(() => (authVersion.value, isAuthenticated())),
  }
}
